import { Box, TextField } from '@mui/material';
import { useAtomValue } from 'jotai';
import queryString from 'query-string';
import { DisplayConfig, Maybe } from '@/__generated__/graphql';
import { useMeasurementTool } from '@/components/Analysis/modules/measurementTool';
import { paintDiameterSharedState } from './state';
import { UnitSystemEnum } from '@/__generated__/graphql';
import { convertSqFeetToSqMeters, localiseMeasurement } from '@/utils/unitSystem';
import { UnitsEnum } from '@/__generated__/graphql';
import { unitSystem as unitSystemState } from '@/components/Analysis/state';
import { ChangeEvent, useCallback, useEffect, useId, useMemo, useState } from 'react';

export type Props = {
  title?: string;
  displayConfig?: Maybe<DisplayConfig>;
  onFieldValueChange?: (value: string) => void;
  value?: string;
};

const EditableProposedPaintArea = ({
  rows,
  onFieldValueChange,
  title,
  value,
}: {
  rows: number;
  onFieldValueChange?: (value: string) => void;
  title: string | undefined;
  value?: string;
}) => {
  const unitSystem = useAtomValue(unitSystemState);
  const defaultValue = useMemo(() => Number(value || 0) || undefined, [value]);
  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      const fieldValue =
        // Always keep the value in meters squared
        unitSystem === UnitSystemEnum.Imperial
          ? convertSqFeetToSqMeters(Number(value))
          : Number(value);
      onFieldValueChange?.(String(fieldValue));
    },
    [onFieldValueChange, unitSystem]
  );

  const label = useMemo(() => {
    return `${title} (${unitSystem === UnitSystemEnum.Imperial ? 'ft²' : 'm²'})`;
  }, [title, unitSystem]);

  const id = useId();

  return (
    <TextField
      id={id}
      label={label}
      multiline={false}
      rows={rows}
      type="number"
      defaultValue={defaultValue}
      onChange={onChange}
    />
  );
};

const AutoGeneratedPaintArea = ({
  rows,
  onFieldValueChange,
  title,
}: {
  rows: number;
  onFieldValueChange?: (value: string) => void;
  title: string | undefined;
}) => {
  const paintDiameter = useAtomValue(paintDiameterSharedState);
  const { measurementDistance } = useMeasurementTool();
  const unitSystem = useAtomValue(unitSystemState);
  const [paintArea, setPaintArea] = useState<string | undefined>('');

  // set paint area to null in case length is changed, calucation of area will happen
  useEffect(() => {
    if (measurementDistance) setPaintArea(undefined);
  }, [measurementDistance]);

  const calculatedPaintArea = useMemo(() => {
    if (paintArea !== undefined) return paintArea;

    const paintDiameterNumber = Number(
      localiseMeasurement({
        unitSystem,
        sourceUnit: UnitsEnum.Meter,
        value: Number(paintDiameter),
        showUnitLabel: false,
        roundToDecimal: 2,
      })
    );

    if (!paintDiameterNumber) return 0;

    const paintDistanceNumber = Number(
      localiseMeasurement({
        unitSystem,
        sourceUnit: UnitsEnum.Meter,
        value: Number(measurementDistance),
        showUnitLabel: false,
        roundToDecimal: 2,
      })
    );

    const result = Math.PI * paintDiameterNumber * paintDistanceNumber;

    if (Number.isNaN(result)) {
      return 0;
    }

    return result.toFixed(2);
  }, [measurementDistance, paintDiameter, unitSystem, paintArea]);

  useEffect(() => {
    if (onFieldValueChange !== undefined && calculatedPaintArea) {
      // Always keep the value in meters squared
      const value =
        unitSystem === UnitSystemEnum.Imperial
          ? convertSqFeetToSqMeters(Number(calculatedPaintArea))
          : Number(calculatedPaintArea);
      onFieldValueChange(String(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatedPaintArea]);

  return (
    <TextField
      id={`TextArea-${title}`}
      label={`${title} (${unitSystem === UnitSystemEnum.Imperial ? 'ft²' : 'm²'})`}
      multiline={false}
      value={calculatedPaintArea}
      rows={rows}
      onChange={(event) => setPaintArea(event.target.value)}
      InputLabelProps={{ shrink: true }}
    />
  );
};

export const ProposedPaintArea = ({ title, displayConfig, onFieldValueChange, value }: Props) => {
  const parsed = queryString.parse(window.location.search, { arrayFormat: 'comma' });
  const insightsTab = parsed?.tab;
  const rows = displayConfig?.textAreaRows || 4;

  return (
    <>
      <Box
        sx={{
          width: '100%',
          '& .MuiTextField-root': { width: '100%' },
        }}
        component="form"
        noValidate
        autoComplete="off"
      >
        {insightsTab ? (
          <EditableProposedPaintArea
            rows={rows}
            onFieldValueChange={onFieldValueChange}
            title={title}
            value={value}
          />
        ) : (
          <AutoGeneratedPaintArea
            rows={rows}
            onFieldValueChange={onFieldValueChange}
            title={title}
          />
        )}
      </Box>
    </>
  );
};
